import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { PrinterOutlined, RedoOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import { useReactToPrint } from "react-to-print";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { parsePhoneNumber } from "react-phone-number-input";

import AppCollapse from "components/ui/AppCollapse.jsx";
import OrderStatusTag from "components/OrderStatusTag";
import { CONSTANTS as C } from "config";
import { UTILS } from "utils";
import { CHECK_BLACKLIST_STATUS } from "api/api";

import Talabat from "../../orderdetail/talabat/Talabat";
import CheckDeliveryModal from "../../orderdetail/Modals/CheckDeliveryModal";
import SelectedIntegrations from "../../../logistic-integrations/SelectedLogistics";
import BlockOrderPlacerModal from "./../modals/BlockOrderPlacerModal";
import LogisticStatus from "./LogisticStatus";
import Invoice from "./Invoice";
import OrderDetailPreview from "./OrderDetailPreview";

/**
 * Full order-detail behavior (refresh, print, logistics, payments, TableItemsDetail, etc.)
 * with OrderDetailsModal-style AppCollapse sections.
 */
const OrderDetailUnifiedBody = ({
  order,
  orderId,
  tabStatus,
  getOrderInfoById,
  showToolbar = true,
  onDeliveryAction,
  showDeliveryActions = true,
  /** When set (e.g. from OrderDetailsModal), print uses this ref on `#orderForPrint` so the parent can trigger printing. */
  printContentRef: printContentRefProp = null,
}) => {
  const [open, setOpen] = useState(false);
  const [talabatModal, setTalabat] = useState(false);
  const [changed, setChanged] = useState(false);
  const [blockOrderPlacerModalVisible, setBlockOrderPlacerModalVisible] =
    useState(false);
  const [isUserBlocked, setIsUserBlocked] = useState(false);
  const [isCheckingBlockStatus, setIsCheckingBlockStatus] = useState(false);
  const [paymentGatewayInfoModal, setPaymentGatewayInfoModal] = useState(false);

  const internalPrintRef = useRef(null);
  const printTargetRef = printContentRefProp ?? internalPrintRef;
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state) => state.LanguageSlice);

  const handlePrint = useReactToPrint({
    content: () => printTargetRef.current,
  });

  useEffect(() => {
    moment.tz.setDefault(moment.tz.guess());
  }, []);

  useEffect(() => {
    if (orderId && typeof getOrderInfoById === "function") {
      getOrderInfoById(orderId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, tabStatus, order?._id]);

  useEffect(() => {
    const checkBlockStatus = async () => {
      if (!order?.userInfo?.phoneNumber) return;
      const parsed = parsePhoneNumber(order.userInfo.phoneNumber);
      let countryCallingCode = "";
      let nationalNumber = "";
      if (parsed) {
        countryCallingCode = parsed.countryCallingCode;
        nationalNumber = parsed.nationalNumber;
      }
      try {
        setIsCheckingBlockStatus(true);
        const response = await CHECK_BLACKLIST_STATUS({
          phoneNumber: nationalNumber,
          countryCode: countryCallingCode,
          brandId: order.brandId,
        });
        setIsUserBlocked(response?.data?.isBlacklisted || false);
      } catch (error) {
        console.error("Error checking block status:", error);
      } finally {
        setIsCheckingBlockStatus(false);
      }
    };
    checkBlockStatus();
  }, [order?.userInfo?.phoneNumber, order?.brandId]);

  const isInterated = (name) => {
    if (!order || !UTILS.isValidObject(["activeIntegrations"], order)) {
      return false;
    }
    if (!Array.isArray(order.activeIntegrations)) return false;
    return order.activeIntegrations.some(
      (el) =>
        el?.name === name &&
        el?.category === "LOGISTIC" &&
        el?.isActive === true
    );
  };

  const getDeliveryAddress = () => {
    let add = "";
    add += order?.deliveryAddress ? order.deliveryAddress.address : "....";
    const hasUnit =
      order?.deliveryAddress?.building ||
      order?.deliveryAddress?.floor ||
      order?.deliveryAddress?.appartmentNo;
    add += hasUnit ? "(" : "";
    add += order?.deliveryAddress?.building
      ? t("Building") + order.deliveryAddress.building + ","
      : "";
    add += order?.deliveryAddress?.floor
      ? t("Floor") + order.deliveryAddress.floor + ","
      : "";
    add += order?.deliveryAddress?.appartmentNo
      ? t("Apartment No") + order.deliveryAddress.appartmentNo
      : "";
    add += hasUnit ? ")" : "";
    return add;
  };

  const getGoogleMapLink = () => {
    const street = order?.deliveryAddress?.street || "";
    return `https://maps.google.com/?q=${street}`;
  };

  const checkIfDeliverySelected = (deliveryType, cb) => {
    onDeliveryAction?.();
    const isSelected = UTILS.getSelectedDelivery(order);
    if (
      isSelected !== C.STORE &&
      isSelected !== deliveryType &&
      deliveryType !== C.TALABAT
    ) {
      setOpen(true);
      setChanged({ fun: cb, type: C.OTHER });
    } else if (
      isSelected !== C.STORE &&
      isSelected !== deliveryType &&
      deliveryType === C.TALABAT
    ) {
      setOpen(true);
      setChanged({ fun: cb, type: C.TALABAT });
    } else if (isSelected !== C.STORE && deliveryType === C.TALABAT) {
      setTalabat(true);
    } else {
      if (isSelected === C.STORE && deliveryType === C.TALABAT) {
        cb(true);
      }
      if (isSelected === C.STORE && deliveryType !== C.TALABAT) {
        cb();
      }
    }
    if (typeof getOrderInfoById === "function") {
      getOrderInfoById(order._id);
    }
  };

  const shouldRenderTalabatComponent = (ord) => ord && isInterated("TALABAT");

  if (!order || !Object.keys(order).length) {
    return null;
  }

  const getOrderPlacedRelative = () =>
    order?.orderPlacedTime
      ? moment.unix(order.orderPlacedTime).fromNow()
      : "N/A";

  const getOrderTypeName = () => {
    if (!order?.orderType) return "N/A";
    return currentLanguage === "EN"
      ? order.orderType.EN?.name
      : order.orderType.AR?.name;
  };

  const getPaymentLabel = () => {
    if (order.isSplitted) return t("Multiple Payments");
    if (!order?.selectedPaymentMethod) return "...";
    return currentLanguage === "EN"
      ? order.selectedPaymentMethod.EN?.name
      : order.selectedPaymentMethod.AR?.name;
  };

  const getOrderSourceLabel = () => {
    return (
      order?.orderingPlatform ||
      order?.orderFrom ||
      order?.source ||
      order?.platform ||
      "...."
    );
  };

  const getOrderTakenByLabel = () => {
    if (typeof order?.orderTakenBy === "string" && order.orderTakenBy.trim()) {
      return order.orderTakenBy;
    }
    if (typeof order?.updatedBy === "string" && order.updatedBy.trim()) {
      return order.updatedBy;
    }
    if (typeof order?.createdBy === "string" && order.createdBy.trim()) {
      return order.createdBy;
    }
    if (
      typeof order?.updatedByName === "string" &&
      order.updatedByName.trim()
    ) {
      return order.updatedByName;
    }
    if (
      typeof order?.createdByName === "string" &&
      order.createdByName.trim()
    ) {
      return order.createdByName;
    }
    if (order?.updatedBy?.name) return order.updatedBy.name;
    if (order?.createdBy?.name) return order.createdBy.name;
    return "...";
  };

  return (
    <div className="relative">
      <Spin spinning={false}>
        <CheckDeliveryModal
          {...{ open, setOpen, changed }}
          deliveryType={order.logisticPartner}
        />
       
        {showToolbar ? (
          <div
            className="mb-3 flex flex-col flex-wrap gap-2 border-b border-gray-100 pb-3 sm:flex-row sm:items-center sm:justify-between"
            key={orderId}
          >
            <div className="text-14px font-medium text-blue-600">
              {order.orderNumber ? t("Order #") + order.orderNumber : "..."}
            </div>
          <div className="sticky top-0 z-[5] border-b border-gray-100 bg-white pb-2 hidden-print">
              {getOrderPlacedRelative()}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded border border-blue-500 bg-white px-2 py-1 text-14px text-blue-500"
                onClick={() => getOrderInfoById?.(orderId)}
              >
                <RedoOutlined />
                <span className="ps-1">{t("Refresh")}</span>
              </button>
              <OrderStatusTag
                status={order.orderStatus ? order.orderStatus : "..."}
              />
              <button
                type="button"
                className="rounded-sm bg-blue-500 px-2 py-1 text-14px text-white"
                onClick={() => handlePrint()}
              >
                <PrinterOutlined />
                <span className="px-2">{t("Print")}</span>
              </button>
            </div>
          </div>
        ) : null}

        {!showToolbar ? (
          <>
            <div className="flex justify-center">
              <div className="text-14px text-gray-500">
                <span className="font-medium text-gray-700">
                  {t("Placed")}:
                </span>{" "}
                {getOrderPlacedRelative()}
              </div>
            </div>
            <div className="mb-2 flex justify-center">
              <div className="text-14px  text-gray-500">
                <span className="font-medium text-gray-700">{t("Type")}:</span>{" "}
                {getOrderTypeName()} |{" "}
                {order?.isPreOrder ? t("PRE ORDER") : t("ASAP")} |{" "}
                <span className="font-medium text-gray-700">
                  {t("Source")}:
                </span>
                : {getOrderSourceLabel()}
              </div>
            </div>
          </>
        ) : null}

        <div
          id="orderForPrint"
          ref={printTargetRef}
          className={`${
            !showToolbar ? "scrollbar max-h-[52vh] overflow-y-auto pe-1" : ""
          }`}
        >
          <div className="print-only">
            <Invoice
              order={order}
              showToolbar={showToolbar}
              getPaymentLabel={getPaymentLabel}
              getOrderTypeName={getOrderTypeName}
              getOrderSourceLabel={getOrderSourceLabel}
              getDeliveryAddress={getDeliveryAddress}
              getGoogleMapLink={getGoogleMapLink}
            getOrderTakenByLabel={getOrderTakenByLabel}
            />
          </div>

          <div className="hidden-print">
            <OrderDetailPreview
              order={order}
              setBlockOrderPlacerModalVisible={setBlockOrderPlacerModalVisible}
              getOrderTypeName={getOrderTypeName}
              getPaymentLabel={getPaymentLabel}
              getOrderSourceLabel={getOrderSourceLabel}
              getDeliveryAddress={getDeliveryAddress}
              getGoogleMapLink={getGoogleMapLink}
              isUserBlocked={isUserBlocked}
              isCheckingBlockStatus={isCheckingBlockStatus}
              getOrderTakenByLabel={getOrderTakenByLabel}
              paymentGatewayInfoModal={paymentGatewayInfoModal}
              setPaymentGatewayInfoModal={setPaymentGatewayInfoModal}
            />
            {showDeliveryActions ? (
              <div className="hidden-print mt-2 ml-3 flex flex-wrap ">
                {shouldRenderTalabatComponent(order) &&
                order?.orderType?.type === C.DELIVERY_SERICETYPE ? (
                  <Talabat
                    {...{
                      order,
                      getOrderInfoById,
                      open: talabatModal,
                      setOpen: setTalabat,
                      checkIfDeliverySelected,
                    }}
                  />
                ) : null}
                {order.orderType?.type === C.DELIVERY_SERICETYPE ? (
                  <SelectedIntegrations
                    {...{ order }}
                    onDeliveryAction={onDeliveryAction}
                  />
                ) : null}
              </div>
            ) : null}
            {order.logisticPartner === "CAREEM_NOW" ||
            order.logisticPartner === "JEEBLY" ||
            order.logisticPartner === "SLIDER" ? (
              <AppCollapse
                title={t("Delivery Status")}
                defaultOpen={true}
                border={false}
              >
                <div className="text-14px font-medium text-gray-400 flex gap-2">
                  <span className="capitalize">
                    {order.logisticPartner?.replace("_", " ")?.toUpperCase()}
                  </span>
                  <LogisticStatus
                    orderId={orderId}
                    logisticPartner={order.logisticPartner}
                  />
                </div>
              </AppCollapse>
            ) : null}
          </div>
        </div>
      </Spin>

      <BlockOrderPlacerModal
        visible={blockOrderPlacerModalVisible}
        onClose={() => setBlockOrderPlacerModalVisible(false)}
        phoneNumber={order?.userInfo?.phoneNumber}
        countryCode={order?.userInfo?.countryCode || "971"}
      />
    </div>
  );
};

OrderDetailUnifiedBody.propTypes = {
  order: PropTypes.object,
  orderId: PropTypes.string,
  tabStatus: PropTypes.string,
  getOrderInfoById: PropTypes.func,
  showToolbar: PropTypes.bool,
  onDeliveryAction: PropTypes.func,
  showDeliveryActions: PropTypes.bool,
  printContentRef: PropTypes.shape({ current: PropTypes.any }),
};

export default OrderDetailUnifiedBody;
